import { type FC, type ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { trackEvent } from 'dibs-tracking';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { ErrorMessage } from 'dibs-elements/exports/ErrorMessage';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { ConnectMetaMaskButton } from 'dibs-crypto-utils/exports/ConnectMetaMaskButton';
import { ConnectWalletConnectButton } from 'dibs-crypto-utils/exports/ConnectWalletConnectButton';
import ExclamationCircle from 'dibs-icons/exports/legacy/ExclamationCircle';
import serverVars from 'server-vars';
import { type WithWalletConnectionEnsuredModalBase_viewer$key } from './__generated__/WithWalletConnectionEnsuredModalBase_viewer.graphql';

import styles from './styles/WithWalletConnectionEnsuredModalBase.scss';

type Props = {
    title: ReactNode;
    warning?: ReactNode;
    paragraphs: Array<ReactNode>;
    showWalletButtons: boolean;
    installTrackingLabel?: string;
    useOpenWalletCopy?: boolean;
    footer?: ReactNode;
    walletButtonsDisabled?: boolean;
    onMetamaskConnect?: () => Promise<void>;
    onWalletConnect?: () => Promise<void>;
    viewer: WithWalletConnectionEnsuredModalBase_viewer$key;
    children?: ReactNode;
    errorMessage?: ReactNode;
};

export const WithWalletConnectionEnsuredModalBase: FC<Props> = ({
    title,
    warning,
    paragraphs,
    showWalletButtons,
    installTrackingLabel,
    walletButtonsDisabled = false,
    useOpenWalletCopy = false,
    onMetamaskConnect,
    onWalletConnect,
    footer,
    children,
    errorMessage,
    viewer: viewerRef,
}) => {
    const isMobile = serverVars.get('settings.isMobile');

    const { connected, openConnectionWithoutSigning, isMetaMaskInstalled } = useWeb3Connection();
    const viewer = useFragment(
        graphql`
            fragment WithWalletConnectionEnsuredModalBase_viewer on Viewer {
                walletConnectFeatureFlag: featureFlag(feature: "allowWalletConnect")
            }
        `,
        viewerRef
    );
    const { walletConnectFeatureFlag } = viewer || false;
    return (
        <>
            <BarHeader title={title} />
            <ModalBody className={styles.modalBody}>
                {warning && (
                    <p className={styles.modalAlert}>
                        <ExclamationCircle className={styles.exclamationIcon} />
                        <span>{warning}</span>
                    </p>
                )}
                {paragraphs.length && (
                    <div className={styles.modalTextWrapper}>
                        {paragraphs
                            .filter(p => !!p)
                            .map((text, i) => (
                                <p key={`generic-copy-modal-body-${i}`}>{text}</p>
                            ))}
                    </div>
                )}
                {children}
                {errorMessage ? (
                    <div className={styles.errorMessageWrapper}>
                        <ErrorMessage dataTn="error-message" message={errorMessage} />
                    </div>
                ) : null}
                {showWalletButtons && (
                    <div className={styles.connectButtons}>
                        {!(isMobile && !isMetaMaskInstalled) && (
                            <ConnectMetaMaskButton
                                fullWidth
                                disabled={walletButtonsDisabled}
                                connected={connected}
                                onConnect={onMetamaskConnect ?? openConnectionWithoutSigning}
                                metaMaskNotInstalled={!isMetaMaskInstalled}
                                useOpenCopy={useOpenWalletCopy}
                                onInstallClick={() =>
                                    trackEvent({
                                        event: 'e_ga_event',
                                        category: 'NFT',
                                        action: 'metamask install click',
                                        label: installTrackingLabel,
                                    })
                                }
                            />
                        )}
                        {walletConnectFeatureFlag && (
                            <ConnectWalletConnectButton
                                fullWidth
                                disabled={walletButtonsDisabled}
                                connected={connected}
                                useOpenCopy={useOpenWalletCopy}
                                onConnect={() => {
                                    if (onWalletConnect) {
                                        onWalletConnect();
                                    } else {
                                        openConnectionWithoutSigning(() => {}, 'walletconnect');
                                    }
                                }}
                            />
                        )}
                    </div>
                )}
                {footer && <div className={styles.modalFooter}>{footer}</div>}
            </ModalBody>
        </>
    );
};
